import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

interface PageTitleContextValue {
  title: string;
  setTitle: (title: string) => void;
  showBack: boolean;
  setShowBack: (show: boolean) => void;
  headerAction: ReactNode;
  setHeaderAction: (action: ReactNode) => void;
  titleAction: ReactNode;
  setTitleAction: (action: ReactNode) => void;
  showCart: boolean;
  setShowCart: (show: boolean) => void;
  showHelp: boolean;
  setShowHelp: (show: boolean) => void;
  titleEditing: boolean;
  setTitleEditing: (editing: boolean) => void;
  onTitleSave: ((name: string) => void) | null;
  setOnTitleSave: (fn: ((name: string) => void) | null) => void;
  hideHeaderTitle: boolean;
  setHideHeaderTitle: (hide: boolean) => void;
  breadcrumb: ReactNode;
  /**
   * Declares a header breadcrumb for the current page. When set, HeaderBar
   * renders it instead of the h2 title. Call setBreadcrumb(null) in the
   * effect cleanup on unmount to avoid stale breadcrumbs on the next page.
   */
  setBreadcrumb: (breadcrumb: ReactNode) => void;
}

const PageTitleContext = createContext<PageTitleContextValue | null>(null);

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState("");
  const [showBack, setShowBack] = useState(false);
  const [headerAction, setHeaderAction] = useState<ReactNode>(null);
  const [titleAction, setTitleAction] = useState<ReactNode>(null);
  const [showCart, setShowCart] = useState(true);
  const [showHelp, setShowHelp] = useState(true);
  const [titleEditing, setTitleEditing] = useState(false);
  const [onTitleSave, setOnTitleSave] = useState<((name: string) => void) | null>(null);
  const [hideHeaderTitle, setHideHeaderTitle] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState<ReactNode>(null);

  return (
    <PageTitleContext.Provider value={{ title, setTitle, showBack, setShowBack, headerAction, setHeaderAction, titleAction, setTitleAction, showCart, setShowCart, showHelp, setShowHelp, titleEditing, setTitleEditing, onTitleSave, setOnTitleSave, hideHeaderTitle, setHideHeaderTitle, breadcrumb, setBreadcrumb }}>
      {children}
    </PageTitleContext.Provider>
  );
}

export function usePageTitle() {
  const ctx = useContext(PageTitleContext);
  if (!ctx) {
    throw new Error("usePageTitle must be used within a PageTitleProvider");
  }
  return ctx;
}
